import http from './http.js';
import { isLogin } from '../utils/auth.js';
import sha256 from 'crypto-js/sha256';
import {
  API_BASE_URL,
  DEFAULT_RESULT_URL,
  MAX_POLL_COUNT,
  DEFAULT_MESSAGES,
  API_PATHS,
} from '../const/constants.js';
import { CACHE_CONFIG } from '../cache/index.js';
import {
  shouldCache,
  getCachedResponse,
  storeCachedResponse,
  trimCache,
  isErrorResponse,
} from '../cache/index.js';

/**
 * 处理 configKey 参数，将具有相同 configKey 的字段合并成一个 JSON 字符串
 * @param {Object} payload - 请求参数
 * @param {Array} paramsList - 参数配置列表
 * @returns {Object} 处理后的 payload
 */
export function processConfigKeyParams(payload, paramsList) {
  if (!paramsList || paramsList.length === 0) return payload;

  const result = { ...payload };
  const configKeyMap = {};

  paramsList.forEach(param => {
    if (param.configKey) {
      const configKey = param.configKey;
      const fieldKey = param.key;

      if (result[fieldKey] !== undefined) {
        if (!configKeyMap[configKey]) {
          configKeyMap[configKey] = {};
        }

        if (fieldKey === configKey && typeof result[fieldKey] === 'string') {
          try {
            const existingObj = JSON.parse(result[fieldKey]);
            configKeyMap[configKey] = { ...configKeyMap[configKey], ...existingObj };
          } catch (e) {
            configKeyMap[configKey][fieldKey] = result[fieldKey];
          }
        } else {
          configKeyMap[configKey][fieldKey] = result[fieldKey];
        }
        delete result[fieldKey];
      }
    }
  });

  Object.keys(configKeyMap).forEach(configKey => {
    result[configKey] = JSON.stringify(configKeyMap[configKey]);
  });

  return result;
}

/**
 * OCR 文字识别基础请求
 * @param {Object} options - 请求配置
 * @param {string} options.cmd - 命令名称
 * @param {Object} options.data - 请求数据
 * @param {boolean} options.isExample - 是否为示例图片
 * @param {string} options.fileType - 文件类型
 * @param {string} options.module - 模块名称
 * @param {boolean} options.checkLogin - 是否检查登录
 * @param {Object} options.config - 请求配置
 * @param {Array} options.paramsList - 参数列表
 * @param {boolean} options.skipCacheStore - 跳过缓存存储（用于轮询场景，由调用方自行决定何时缓存）
 */
export async function baseOcrRequest({ cmd, data, isExample = false, fileType, module, checkLogin = true, config, paramsList, skipCacheStore = false }) {
  // 构建请求配置对象
  const requestConfig = { cmd, data, isExample, fileType, module, checkLogin, config, paramsList };
  
  // 检查是否应该尝试从缓存读取
  if (shouldCache(requestConfig, CACHE_CONFIG)) {
    const cachedResponse = await getCachedResponse(requestConfig, CACHE_CONFIG);
    if (cachedResponse) {
      console.log(`[OCR Cache] 缓存命中: ${cmd}`);
      return cachedResponse;
    }
  }
  
  // 没有缓存或缓存过期，继续正常请求流程
  let url = isExample ? API_PATHS.DEMO_RESULT_BY_DEFAULT_IMAGE : API_PATHS.GET_RESULT;
  let payload = {
    ...data
  };

  if (paramsList) {
    payload = processConfigKeyParams(payload, paramsList);
  }

  // 过滤掉值为 null/undefined 的顶层字段，避免无意义空参数发送到后端
  Object.keys(payload).forEach((k) => {
    if (payload[k] === null || payload[k] === undefined) {
      delete payload[k];
    }
  });

  if (cmd === 'ReconstructDocument') {
    payload.Config = {
      EnableInsetImage: data.EnableInsetImage === '1' ? true : false
    };
    delete payload.EnableInsetImage;
  } else if (cmd === 'ReconstructDocumentDemo') {
    payload.FileType = fileType.toUpperCase();
  }

  const response = await http.post({
    url: `${API_BASE_URL}${url}`,
    cmd,
    data: payload,
    module,
    checkLogin,
    config
  });
  
  // 存储响应到缓存（如果是示例图片且不跳过缓存存储且不是错误响应）
  // skipCacheStore 用于轮询场景，由调用方在任务成功完成时自行调用缓存存储
  // isErrorResponse 检查已在 storeCachedResponse 内部处理，这里只做日志区分
  if (!skipCacheStore && shouldCache(requestConfig, CACHE_CONFIG)) {
    if (isErrorResponse(response)) {
      console.log(`[OCR Cache] 跳过缓存错误响应: ${cmd}`);
    } else {
      try {
        await storeCachedResponse(requestConfig, response, CACHE_CONFIG);
        console.log(`[OCR Cache] 缓存已存储: ${cmd}`);
      } catch (error) {
        console.error('[OCR Cache] 存储缓存失败:', error);
      }
    }
  }
  
  return response;
}

/**
 * 获取产品列表
 */
export async function getProductList() {
  const url = API_PATHS.GET_PRODUCT_CONFIG;
  return http.demoPost({
    url: `${API_BASE_URL}${url}`
  });
}

/**
 * 轮询 API
 */
export function pollApi(payload, interval, maxPollCount = MAX_POLL_COUNT) {
  let pollCount = 0;
  let canceled = false;

  return {
    start: new Promise((resolve, reject) => {
      const executePoll = () => {
        if (canceled) return;
        if (pollCount >= maxPollCount) {
          reject({
            msg: DEFAULT_MESSAGES.NETWORK_ERROR
          });
          return;
        }

        pollCount++;

        baseOcrRequest(payload)
          .then(response => {
            if (canceled) return;
            if (response.Error || response.error || response.JobStatus === 'FAIL') {
              reject(response);
            } else if (response.JobStatus === 'DONE') {
              resolve(response);
            } else {
              resolve(response);
              setTimeout(executePoll, interval);
            }
          })
          .catch(error => {
            if (canceled) return;
            reject(error);
          });
      };

      executePoll();
    }),
    cancel: () => {
      canceled = true;
    }
  };
}

/**
 * 获取最近访问菜单
 */
export async function getRecentMenu(data) {
  const { QCLogin } = window;
  if (!isLogin) return;
  let uin = QCLogin.getUin();
  return http.post({
    url: DEFAULT_RESULT_URL,
    cmd: 'QueryUserInterfaceHistoryAccessRecordForDemo',
    data: {
      ...data,
      Sid: sha256(uin).toString()
    },
    checkLogin: false
  });
}

/**
 * 添加最近访问菜单
 */
export async function addRecentMenu(data) {
  const { QCLogin } = window;
  if (!isLogin) throw ({
    error: true
  });
  let uin = QCLogin.getUin();

  return http.post({
    url: DEFAULT_RESULT_URL,
    cmd: 'AddUserInterfaceHistoryAccessRecordForDemo',
    data: {
      ...data,
      Sid: sha256(uin).toString()
    },
    checkLogin: false
  });
}
